import { TFunction } from 'i18next'
import { z } from 'zod'

import { BookingType } from '@/api/booking-types'
import { BOOKING_TYPE_IMAGE_SIZE, LINK_REGEX } from '@/helpers/constants'

import { BOOKING_NAME_MAX_LENGTH } from './group-form-modal.constants'

export const BOOKING_TYPE_TEXT_MAX_LENGTH = 255

// Note: wide enough for 10 icon options per row (5.2rem each, 0.8rem gap) so the 20 icons take two rows
export const BOOKING_TYPE_FORM_WIDTH = '60rem'

export const BOOKING_TYPE_IMAGE_PATH = 'chitown-images/booking'

interface BookingTypeSchemaOptions {
  hasExistingImage: boolean
}

export const makeBookingTypeFormSchema = (
  t: TFunction<['organisation', 'common']>,
  { hasExistingImage }: BookingTypeSchemaOptions,
) => {
  const maxLength = (max: number) => t('organisation:bookingTypes.validation.maxLength', { max })

  const requiredName = (max: number) =>
    z.string().trim().min(1, t('common:validation.required')).max(max, maxLength(max))

  return z
    .object({
      buildingIds: z
        .array(z.number())
        .min(1, t('organisation:bookingTypes.validation.buildingsRequired')),
      color: z.string().min(1, t('organisation:bookingTypes.validation.colorRequired')),
      description: z
        .string()
        .trim()
        .max(BOOKING_TYPE_TEXT_MAX_LENGTH, maxLength(BOOKING_TYPE_TEXT_MAX_LENGTH)),
      icon: z.string().min(1, t('organisation:bookingTypes.validation.iconRequired')),
      image: z.array(z.instanceof(File)).optional(),
      internalName: requiredName(BOOKING_NAME_MAX_LENGTH),
      link: z
        .string()
        .trim()
        .max(BOOKING_TYPE_TEXT_MAX_LENGTH, maxLength(BOOKING_TYPE_TEXT_MAX_LENGTH))
        .refine((value) => !value || LINK_REGEX.test(value), {
          message: t('organisation:bookingTypes.validation.linkInvalid'),
        }),
      name: requiredName(BOOKING_NAME_MAX_LENGTH),
    })
    .superRefine((data, ctx) => {
      const file = data.image?.[0]

      if (!file && !hasExistingImage) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:bookingTypes.validation.imageRequired'),
          path: ['image'],
        })
      }

      if (file && file.size > BOOKING_TYPE_IMAGE_SIZE) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:bookingTypes.validation.imageTooLarge'),
          path: ['image'],
        })
      }
    })
}

export type BookingTypeFormData = z.infer<ReturnType<typeof makeBookingTypeFormSchema>>

export const getBookingTypeFormDefaults = (
  bookingType?: BookingType,
  { duplicate = false }: { duplicate?: boolean } = {},
): BookingTypeFormData => ({
  buildingIds: bookingType?.buildings?.map(({ id }) => id) ?? [],
  color: bookingType?.color ?? '',
  description: bookingType?.description ?? '',
  icon: bookingType?.icon ?? '',
  image: undefined,
  internalName: duplicate ? '' : (bookingType?.internalName ?? ''),
  link: bookingType?.link ?? '',
  name: bookingType?.name ?? '',
})
